import { FunctionComponent, useContext, useMemo, useState } from 'react';
import { Edge, Handle, NodeProps, Position, useEdges } from 'reactflow';
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Box,
  Tooltip,
  useTheme,
  lighten,
} from '@mui/material';

import { EditorContext } from '../context';
import { ReactComponent as WifiIcon } from '../../../../assets/icons/main/gateway-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/main/delete-icon.svg';
import { ReactComponent as ConfigurationIcon } from '../../../../assets/icons/main/configuration-icon.svg';
import { ReactComponent as AddCircleIcon } from '../../../../assets/icons/main/plus-circle-icon.svg';
import { ReactComponent as PlcIcon } from '../../../../assets/icons/main/plc-icon.svg';
import { ReactComponent as ExcelIcon } from '../../../../assets/icons/main/microsoft-excel-icon.svg';
import { AssetNode } from './Asset';
import { AssetSchema, AssetUISchema } from '../panel-schemas';
import * as uuid from 'uuid';
import { palette } from '../../../../theme/palette';
import { useOutletContext } from 'react-router-dom';
import { get } from 'lodash';
import ElipsisTypography from '../components/ElipsisTypography';

import NodeStatuses from './NodeStatuses';

const Styles = {
  nodeWrapper: {
    height: '48px',
    position: 'relative',
    borderRadius: '12px',
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '300px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '11px 0 0 11px',
    backgroundColor: palette.success.main,
  },
  icon: {
    height: '48px',
    width: '48px',
  },
  handleSource: {
    height: '1px',
    width: '1px',
    backgroundColor: 'transparent',
    left: 0,
  },
  handleTarget: {
    height: '1px',
    width: '1px',
    backgroundColor: 'transparent',
    right: 0,
  },
  addIcon: {
    position: 'absolute',
    left: '-36px',
  },
  addMenuPaper: {
    borderRadius: '8px',
    border: '1px solid #D9DFED',
    boxShadow: '#0000000D',
    overflow: 'visible',
  },
  addMenuList: {
    padding: '18px 18px',
  },
  addMenuItem: {
    padding: '0px',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  },
  addMenuIcon: {
    marginRight: '8px',
     minWidth: 'auto',
  },
  addMenuText: {
    fontSize: '1rem',
    fontWeight: 400,
    color: '#64759F',
  },
  addMenuDivider: { width: '100%' },
  edgeWrapper: { width: '355px', borderRadius: '18px', borderBottom: '6px solid #68CB00' },
  header: {
    height: '79px',
    display: 'flex',
    alignItems: 'center',
    padding: '24px 24px 14px 24px',
    borderBottom: '1px dashed #68CB00',
    boxSizing: 'border-box',
  },
  scanContainer: {
    height: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    backgroundColor: '#F8FAFB',
    padding: '24px 24px 0px 24px',
    boxSizing: 'border-box',
  },
  scanText: { fontSize: '1rem', fontWeight: 400, color: '#434D67' },
  scanWrapper: { display: 'flex', justifyContent: 'space-between' },
  scanField: { width: '182px', height: '42px' },
  scanBtn: { width: '109px', height: '42px', fontSize: '1rem', backgroundColor: '#68CB00' },
  btnsWrapper: {
    minHeight: '24px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '24px',
    boxSizing: 'border-box',
  },
  btn: { width: '307px', height: '42px', color: '#64759F', borderColor: '#64759F' },
  divider: { width: '307px' },
};

const renderIcon = (type: string) => {
  switch (type) {
    case 'addComponent':
      return <AddCircleIcon />;
    case 'businessRules':
      return <ConfigurationIcon />;
    default:
      return <></>;
  }
};

export const EdgeNode: FunctionComponent<NodeProps> = ({ id, data = {}, xPos, yPos }: NodeProps) => {
  const { addNode, removeNode, editable, useCase, updateData, scanAssets, scanning, onBtnClick, hasAccessTo, isLiveView, onGatewayImportClick } =
    useContext(EditorContext);
  const themeData: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});

  const hasReadOnlyAccess = !(hasAccessTo.create && hasAccessTo.update);
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  const edges = useEdges();
  const thisEdge: Edge = useMemo(() => edges.find((edge: Edge) => edge.target === id) as Edge, [id, edges]);

  return (
    <>
      <Handle style={Styles.handleTarget} type='target' position={Position.Right} />
      <Handle style={Styles.handleSource} type='source' position={Position.Left} />
      <Paper
        elevation={0}
        sx={{
          ...Styles.nodeWrapper,
          border: `1px solid ${themeData.palette.customBorderColor.main}`,
          backgroundColor: themeMode.isLightBG
            ? themeData.palette.background.paper
            : lighten(themeData.palette.background.default, 0.2),
        }}
      >
        {editable && (
          <>
            <Tooltip title='Add / Import Assets'>
              <IconButton
                sx={Styles.addIcon}
                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                disabled={hasReadOnlyAccess}
              >
                <AddCircleIcon
                  style={{ color: themeData.palette.custom.light, opacity: hasReadOnlyAccess ? '0.5' : 1 }}
                />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
              anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: Styles.addMenuPaper }}
              MenuListProps={{
                sx: {
                  ...Styles.addMenuList,
                  '& .MuiMenuItem-root .MuiListItemIcon-root': {
                    minWidth: '0 !important',
                  },
                },
              }}
            >
              <MenuItem
                sx={Styles.addMenuItem}
                onClick={() => {
                  setAddMenuAnchor(null);
                  addNode(
                    {
                      id: uuid.v4().toString(),
                      type: 'assetNode',
                      display: 'Asset',
                      node: AssetNode,
                      sourcePosition: Position.Right,
                      targetPosition: Position.Left,
                      panel: {
                        schema: AssetSchema,
                        uiSchema: AssetUISchema,
                      },
                      data: { label: 'Asset' },
                      connectedTo: id,
                    },
                    id
                  );
                }}
              >
                <ListItemIcon sx={Styles.addMenuIcon}>
                  <PlcIcon style={{ width: 20, height: 20, color: themeData.palette.custom.light }} />
                </ListItemIcon>
                <ListItemText
                  primary='Add Asset'
                  primaryTypographyProps={{ sx: Styles.addMenuText }}
                  sx={{ margin: 0 }}
                />
              </MenuItem>
              <Box sx={{ my: '16px' }}>
                <Divider sx={Styles.addMenuDivider} />
              </Box>
              <MenuItem
                sx={Styles.addMenuItem}
                onClick={() => {
                  setAddMenuAnchor(null);
                  onGatewayImportClick(String(data.gateway_id ?? id), data);
                }}
              >
                <ListItemIcon sx={Styles.addMenuIcon}>
                  <ExcelIcon style={{ width: 20, height: 20, color: themeData.palette.custom.light }} />
                </ListItemIcon>
                <ListItemText
                  primary='Import Assets'
                  primaryTypographyProps={{ sx: Styles.addMenuText }}
                  sx={{ margin: 0 }}
                />
              </MenuItem>
            </Menu>
          </>
        )}
        <Box sx={Styles.iconWrapper}>
          <IconButton
            disabled={!editable && !useCase} //hasReadOnlyAccess
            style={Styles.icon}
            onClick={() => onBtnClick('edgeNode', { id, data })}
          >
            <WifiIcon style={{ color: palette.primary.contrastText }} />
          </IconButton>
        </Box>
        <Typography
          sx={{
            fontSize: '1rem',
            marginLeft: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 90px)',
          }}
        >
          <ElipsisTypography width={250}> {data.name ?? 'Gateway'} </ElipsisTypography>
          {/* {'Gateway'} */}
        </Typography>
        {editable ? (
          <IconButton disabled={!editable || hasReadOnlyAccess} onClick={() => removeNode(id)}>
            <DeleteIcon style={{ color: themeData.palette.custom.light, opacity: hasReadOnlyAccess ? '0.5' : 1 }} />
          </IconButton>
        ) : (
          <Box sx={{ width: 38 }} />
        )}
        {/*<Box sx={Styles.scanContainer}>*/}
        {/*  <Typography sx={Styles.scanText}>Mention CIDR to Find connected assets.</Typography>*/}
        {/*  <Box sx={Styles.scanWrapper}>*/}
        {/*    <TextField disabled={!editable} value={data.cidr_to_scan} onChange={(e) => updateData({ cidr_to_scan: e.target.value})} sx={Styles.scanField} />*/}
        {/*    <LoadingButton disabled={!editable} loading={scanning.includes(data.gateway_id)} onClick={() => scanAssets(data.cidr_to_scan)} variant="contained" startIcon={<ScanIcon />} sx={Styles.scanBtn}>*/}
        {/*      Scan*/}
        {/*    </LoadingButton>*/}
        {/*  </Box>*/}
        {/*</Box>*/}
        {/*<Box sx={Styles.btnsWrapper}>*/}
        {/*  {(data.options || []).map((option: any, i: number) => (*/}
        {/*    <>*/}
        {/*      {i !== 0 && <Divider sx={Styles.divider} />}*/}
        {/*      <Button*/}
        {/*        variant="outlined"*/}
        {/*        startIcon={renderIcon(option.id)}*/}
        {/*        sx={Styles.btn}*/}
        {/*        onClick={() => {*/}
        {/*          onBtnClick('edgeNode', option);*/}
        {/*        }}*/}
        {/*      >*/}
        {/*        {option.label}*/}
        {/*      </Button>*/}
        {/*    </>*/}
        {/*  ))}*/}
        {/*</Box>*/}

        {isLiveView && (
          <NodeStatuses
            statuses={{
              mqttStatus: thisEdge?.data?.mqttStatus,
              plcStatus: thisEdge?.data?.plcStatus,
              dbStatus: thisEdge?.data?.dbStatus,
            }}
            palette={themeData.palette}
          />
        )}
      </Paper>
    </>
  );
};
